import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { useDashboardStore } from '../../store/dashboardStore';
import { useAuthStore } from '../../store/authStore';
import ChartWidget from '../../components/charts/ChartWidget';
import AddChartModal from '../../components/modals/AddChartModal';
import AIChartBuilder from '../../components/modals/AIChartBuilder';
import ShareDashboardModal from '../../components/modals/ShareDashboardModal';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  ArrowLeftIcon,
  Cog6ToothIcon,
  ShareIcon,
  SparklesIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const ResponsiveGridLayout = WidthProvider(Responsive);

export default function DashboardBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    currentDashboard, 
    charts, 
    layout, 
    isLoading, 
    fetchDashboard, 
    updateLayout,
    updateDashboard,
    resetCurrent 
  } = useDashboardStore();
  const { hasPermission } = useAuthStore();
  
  const [isEditing, setIsEditing] = useState(false);
  const [showAddChart, setShowAddChart] = useState(false);
  const [showAIChartBuilder, setShowAIChartBuilder] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [chartRefreshKey, setChartRefreshKey] = useState(0); // Used to trigger all charts to refresh
  const isMountedRef = useRef(true);
  
  // Permission checks
  const canEditDashboard = hasPermission('edit_dashboards');
  const canCreateCharts = hasPermission('create_dashboards'); // Charts require dashboard creation permission
  const canShareDashboards = hasPermission('share_dashboards');
  
  // Stable fetch function that handles errors and retries
  const loadDashboard = useCallback(async (dashboardId, isRetry = false) => {
    if (!dashboardId || dashboardId === 'new') return;
    
    try {
      await fetchDashboard(dashboardId);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      // Retry once on failure
      if (!isRetry && isMountedRef.current) {
        console.log('[DashboardBuilder] Retrying dashboard fetch...');
        setTimeout(() => {
          if (isMountedRef.current) {
            loadDashboard(dashboardId, true);
          }
        }, 1000);
      } else if (isMountedRef.current) {
        toast.error('Failed to load dashboard. Please try refreshing.');
      }
    }
  }, [fetchDashboard]);
  
  useEffect(() => {
    isMountedRef.current = true;
    
    if (id && id !== 'new') {
      loadDashboard(id);
    }
    
    return () => {
      isMountedRef.current = false;
      resetCurrent();
    };
  }, [id, loadDashboard, resetCurrent]);
  
  // Manual refresh handler - also triggers all charts to refresh their data
  const handleRefreshDashboard = useCallback(async () => {
    if (!id || id === 'new' || isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await fetchDashboard(id);
      // Increment refresh key to trigger all ChartWidgets to refetch their data
      setChartRefreshKey(prev => prev + 1);
      toast.success('Dashboard refreshed');
    } catch (error) {
      toast.error('Failed to refresh dashboard');
    } finally {
      if (isMountedRef.current) {
        setIsRefreshing(false);
      }
    }
  }, [id, fetchDashboard, isRefreshing]);
  
  // Track current responsive breakpoint (ref for layout guard, state for UI re-renders)
  const currentBreakpointRef = useRef('lg');
  const [isDesktop, setIsDesktop] = useState(true);
  
  const handleLayoutChange = useCallback((currentLayout, allLayouts) => {
    // Only persist layout changes from the desktop (lg) breakpoint
    // to prevent auto-generated mobile layouts from overwriting the saved arrangement
    if (currentBreakpointRef.current !== 'lg') return;
    
    const formattedLayout = currentLayout.map(item => ({
      chartId: item.i,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h
    }));
    updateLayout(formattedLayout);
  }, [updateLayout]);
  
  const handleNameChange = async (e) => {
    if (e.key === 'Enter' || e.type === 'blur') {
      const newName = e.target.value.trim();
      if (newName && newName !== currentDashboard?.name) {
        try {
          await updateDashboard(id, { name: newName });
        } catch (error) {
          toast.error('Failed to update name');
        }
      }
      setIsEditing(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <div className="flex items-center gap-4 mb-8">
          <div className="skeleton h-8 w-48" />
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="skeleton h-64" />
          <div className="skeleton h-64" />
          <div className="skeleton h-64" />
          <div className="skeleton h-64" />
        </div>
      </div>
    );
  }
  
  const gridLayout = layout.map(item => ({
    i: item.chartId?.toString() || item.i,
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
    minW: 2,
    minH: 2
  }));
  
  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/dashboards')}
            className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
          >
            <ArrowLeftIcon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
          </button>
          
          {isEditing && canEditDashboard ? (
            <input
              type="text"
              defaultValue={currentDashboard?.name}
              autoFocus
              onKeyDown={handleNameChange}
              onBlur={handleNameChange}
              className="text-2xl font-display font-bold bg-transparent border-b-2 border-primary-500 outline-none text-surface-900 dark:text-white"
            />
          ) : (
            <h1 
              onClick={() => canEditDashboard && setIsEditing(true)}
              className={`text-2xl font-display font-bold text-surface-900 dark:text-white ${canEditDashboard ? 'cursor-pointer hover:text-primary-600 transition-colors' : ''}`}
            >
              {currentDashboard?.name || 'Untitled Dashboard'}
            </h1>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={handleRefreshDashboard}
            disabled={isRefreshing || isLoading}
            className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 disabled:opacity-50"
            title="Refresh Dashboard"
          >
            <ArrowPathIcon className={`w-5 h-5 text-surface-600 dark:text-surface-400 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          {canShareDashboards && id && id !== 'new' && (
            <button
              onClick={() => setShowShareModal(true)}
              className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
              title="Share Dashboard"
            >
              <ShareIcon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
            </button>
          )}
          {canCreateCharts && (
            <>
              <button 
                onClick={() => setShowAIChartBuilder(true)}
                className="btn-secondary"
              >
                <SparklesIcon className="w-5 h-5 mr-2" />
                Create with AI
              </button>
              <button onClick={() => setShowAddChart(true)} className="btn-primary">
                <PlusIcon className="w-5 h-5 mr-2" />
                Add Chart
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Dashboard grid */}
      <div id="dashboard-container" className="min-h-[600px]">
        {charts.length === 0 ? (
          <div className="card p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-surface-100 dark:bg-surface-800 rounded-full flex items-center justify-center">
              <PlusIcon className="w-8 h-8 text-surface-400" />
            </div>
            <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
              {canCreateCharts ? 'Add your first chart' : 'No charts yet'}
            </h3>
            <p className="text-surface-500 dark:text-surface-400 mb-6">
              {canCreateCharts 
                ? 'Start building your dashboard by adding charts and visualizations'
                : 'This dashboard has no charts. Contact an admin to add visualizations.'}
            </p>
            {canCreateCharts && (
              <div className="flex items-center justify-center gap-4">
                <button onClick={() => setShowAddChart(true)} className="btn-primary">
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Add Chart
                </button>
                <button 
                  onClick={() => setShowAIChartBuilder(true)}
                  className="btn-secondary"
                >
                  <SparklesIcon className="w-5 h-5 mr-2" />
                  Create with AI
                </button>
              </div>
            )}
          </div>
        ) : (
          <ResponsiveGridLayout
            className="layout"
            layouts={{ lg: gridLayout }}
            breakpoints={{ lg: 768, md: 480, sm: 0 }}
            cols={{ lg: 12, md: 6, sm: 1 }}
            rowHeight={50}
            margin={[16, 16]}
            containerPadding={[0, 0]}
            onLayoutChange={canEditDashboard ? handleLayoutChange : undefined}
            onBreakpointChange={(newBreakpoint) => {
              currentBreakpointRef.current = newBreakpoint;
              setIsDesktop(newBreakpoint === 'lg');
            }}
            draggableHandle=".drag-handle"
            isResizable={canEditDashboard && isDesktop}
            isDraggable={canEditDashboard && isDesktop}
          >
            {charts.map(chart => (
              <div key={chart._id} className="card overflow-hidden h-full">
                <ChartWidget chart={chart} refreshKey={chartRefreshKey} />
              </div>
            ))}
          </ResponsiveGridLayout>
        )}
      </div>
      
      {/* Add Chart Modal */}
      <AddChartModal 
        isOpen={showAddChart} 
        onClose={() => setShowAddChart(false)}
        dashboardId={id}
      />
      
      {/* AI Chart Builder */}
      <AIChartBuilder
        isOpen={showAIChartBuilder}
        onClose={() => setShowAIChartBuilder(false)}
        dashboardId={id}
        onChartCreated={(chart) => {
          // Refresh the dashboard to show the new chart
          fetchDashboard(id);
          toast.success('AI Chart added to dashboard!');
        }}
      />

      {/* Share Dashboard Modal */}
      <ShareDashboardModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        dashboardId={id}
        charts={charts}
      />
    </div>
  );
}

